import { useState, useCallback } from 'react';
import { ChevronDown, X } from 'lucide-react';

interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

interface FilterPanelProps {
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  isOpen?: boolean;
}

export function FilterPanel({
  title,
  options,
  selectedValues,
  onSelectionChange,
  isOpen = true,
}: FilterPanelProps) {
  const [expanded, setExpanded] = useState(isOpen);

  const handleSelectAll = useCallback(() => {
    if (selectedValues.length === options.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(options.map((o) => o.value));
    }
  }, [selectedValues, options, onSelectionChange]);

  const handleClear = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  const handleChange = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newValues);
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 bg-slate-100 border-none flex items-center justify-between cursor-pointer font-medium text-sm text-gray-700 hover:bg-slate-200 transition-colors"
      >
        <span>
          {title}
          {selectedValues.length > 0 && (
            <span className="ml-2 text-blue-700 font-semibold">
              ({selectedValues.length})
            </span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${expanded ? 'rotate-180' : 'rotate-0'}`}
        />
      </button>

      {expanded && (
        <div className="p-3 border-t border-slate-200">
          <div className="flex gap-2 mb-3">
            <button
              onClick={handleSelectAll}
              className={`flex-1 px-3 py-1.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                selectedValues.length === options.length
                  ? 'bg-blue-700 text-white'
                  : 'bg-slate-100 text-gray-700 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            <button
              onClick={handleClear}
              className={`flex-1 px-3 py-1.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                selectedValues.length === 0
                  ? 'bg-slate-100 text-slate-500'
                  : 'bg-red-100 text-red-900 hover:bg-red-200'
              }`}
            >
              Clear
            </button>
          </div>

          <div className="flex flex-col gap-2">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 cursor-pointer p-1 text-sm text-gray-700 hover:bg-slate-50 rounded transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleChange(option.value)}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="flex-1">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-xs text-slate-400">{option.count}</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface FilterSidebarProps {
  filters: {
    [key: string]: {
      title: string;
      options: FilterOption[];
      selectedValues: string[];
    };
  };
  onFilterChange: (filterKey: string, values: string[]) => void;
  onReset: () => void;
}

export function FilterSidebar({ filters, onFilterChange, onReset }: FilterSidebarProps) {
  const totalActiveFilters = Object.values(filters).reduce(
    (sum, f) => sum + f.selectedValues.length,
    0
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="m-0 text-base font-bold text-slate-900">Filters</h3>
        {totalActiveFilters > 0 && (
          <button
            onClick={onReset}
            className="px-3 py-1 bg-red-100 text-red-900 border-none rounded text-xs font-medium cursor-pointer hover:bg-red-200 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <X size={14} />
              Clear all
            </span>
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {Object.entries(filters).map(([key, filter]) => (
          <FilterPanel
            key={key}
            title={filter.title}
            options={filter.options}
            selectedValues={filter.selectedValues}
            onSelectionChange={(values) => onFilterChange(key, values)}
          />
        ))}
      </div>
    </div>
  );
}
